import { serialize } from "object-to-formdata";
import request from "../../utils/request";

import {
 ClockingType, ClockingInput, ClockingView, ClockingStatusView, ClockingPaginationView, EditClockingInput, OvertimeShiftApprovalInput, TimesheetTableView, ShiftInput, ShiftView, ClockOutInput, ShiftHistoryView, WialonShiftTrack, EffectiveClockingConfigView, OpenShiftView
} from "../../models/attendance";
import { Result, UserView } from "../../models/general";

export async function getAllowedActions() {
  const response = await request.get<Result<ClockingType[]>>("attendance/allowedactions");
  return response.data;
}

export async function clock(type: ClockingType, input: ClockingInput) {
  const response = await request.post<Result<ClockingView>>(`Attendance/${type}`,
    serialize(input));
  return response.data;
}

export async function verifyOvertime(shiftId : number) {
  const response = await request.post<Result<never>>(`attendance/VerifyOvertime/${shiftId}`);
  return response.data;
}
export async function verifyInBreak(shiftId : number) {
  const response = await request.post<Result<never>>(`attendance/VerifyInBreak/${shiftId}`);
  return response.data;
}

export async function editClockTime(id: number, input: EditClockingInput) {
  const response = await request.post<Result<ClockingView>>(`attendance/edit/${id}`,
    serialize(input));
  return response.data;
}

export async function getStatus() {
  const response = await request.get<Result<ClockingStatusView>>("attendance/status");
  return response.data;
}

// Caller-scoped geofence/out-of-bounds config (effective policy, buffer, reason presets, and the
// polygons this user clocks against). Lets the clock dialog pre-detect an out-of-bounds punch.
export async function getEffectiveClockingConfig() {
  const response = await request.get<Result<EffectiveClockingConfigView>>("Attendance/EffectiveConfig");
  return response.data;
}

export async function getClockings(id: string, from: string, to: string) {
  const response = await request.get<Result<ClockingPaginationView>>("attendance", {
    params: { from, to, userId: id },
  });
  return response.data;
}

export async function getClockingsPerDay(id: string, from: string, to: string) {
  const response = await request.get<Result<ClockingView[][]>>("attendance/perday", {
    params: { from, to, userId: id },
  });
  return response.data;
}

export async function getTimesheetTableData(id: string, from: string, to: string) {
  const response = await request.get<Result<TimesheetTableView>>("attendance/TimesheetTableData", {
    params: { from, to, userId: id },
  });
  return response.data;
}

export async function overtimeApproval(input: OvertimeShiftApprovalInput) {
  const response = await request.post<Result<ClockingView>>(`attendance/OvertimeShiftApproval`, input);
  return response.data;
}

export async function checkIsManagerAndShiftNotArchived(userId: string, shiftId: number) {
  const response = await request.get<Result<boolean>>(`attendance/CheckIsManagerAndShiftNotArchived/${userId}/${shiftId}`);
  return response.data;
}


export async function getCurrentStatuses() {
  const response = await request.get<Result<UserView[]>>("attendance/UsersCurrentStatus");
  return response.data;
}

export async function addShift(input : ShiftInput, hoursOffset : number) {
  const response = await request.post<Result<ShiftView>>(`attendance/addShift/${hoursOffset}`, input);
  return response.data;
}

/**
 * Open (clock-out-less) shifts for the caller's company inside a window. HR/Admin only.
 *
 * The team-attendance grid is built from the UsersStatuses report, whose shift query drops any row
 * without a clock-out — so a half-entered backfill renders "-" for BOTH punches and HR gets no signal
 * it exists. This is how the grid gets those rows back.
 * `from` / `to` are "yyyy-MM-dd".
 */
export async function getOpenShifts(from: string, to: string, userIds?: string[]) {
  // Built by hand rather than through axios' `params`: this project is on axios 0.21, whose default
  // array serializer emits `userIds[]=a&userIds[]=b`, which ASP.NET Core will NOT bind to a
  // `List<string> userIds` parameter. Repeated bare keys is the form the model binder accepts.
  const qs = new URLSearchParams();
  qs.append("from", from);
  qs.append("to", to);
  for (const id of userIds || []) {
    if (id) qs.append("userIds", id);
  }
  const response = await request.get<Result<OpenShiftView[]>>(`Attendance/OpenShifts?${qs.toString()}`);
  return response.data;
}

export async function updateAttendanceRemark(input: { userId: string; date: Date | string; remark: string }) {
  const response = await request.post<Result<boolean>>('attendance/remark', input);
  return response.data;
}
export async function getShiftHistory(id : number, type: ClockingType) {
  const response = await request.get<Result<ShiftHistoryView[]>>(`attendance/ShiftHistory/${id}/${type}`);
  return response.data;
}

export async function getShiftTrack(shiftId: number) {
  const response = await request.get<Result<WialonShiftTrack>>(`attendance/ShiftTrack/${shiftId}`);
  return response.data;
}

export async function logFaceRecognition(data: {
  isRecognized: boolean;
  livenessVerified: boolean;
  matchConfidence?: number;
  livenessScore?: number;
}) {
  const response = await request.post<Result<boolean>>('attendance/LogFaceRecognition', data);
  return response.data;
}

